<template>
  <!-- Agency Phase 1 — the workspace switcher, mounted in LhsHeaderCard's
       `ws-row` slot. Rendered ONLY when the user belongs to more than one
       workspace; a single-workspace user gets nothing here, so their rail is
       byte-identical to before. -->
  <div v-if="workspaces.length > 1" class="ws-switcher" :class="{ 'is-open': open }">
    <button
      ref="triggerEl"
      type="button"
      class="ws-switcher__trigger"
      :title="activeName"
      @click="toggle"
    >
      <CygnusIcon name="building" class="ws-switcher__icon" />
      <span class="ws-switcher__label">{{ activeName }}</span>
      <CygnusIcon name="chevron-down" class="ws-switcher__chevron" />
    </button>

    <teleport to="body">
      <div v-if="open" class="ws-switcher__backdrop" @click.self="close">
        <div class="ws-switcher__panel" :style="panelStyle" role="menu">
          <div class="ws-switcher__panel-head">Switch workspace</div>
          <button
            v-for="w in workspaces"
            :key="w.tenant_id"
            type="button"
            class="ws-switcher__item"
            :class="{ 'is-active': w.tenant_id === activeTenantId }"
            :disabled="switching"
            @click="select(w)"
          >
            <span class="ws-switcher__item-name">{{ w.name }}</span>
            <span v-if="w.is_home" class="ws-switcher__badge">Home</span>
            <CygnusIcon name="check" class="ws-switcher__check" v-if="w.tenant_id === activeTenantId" />
          </button>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script>
import useApi from '@apexl/icarus-core/src/services/api';

/**
 * WorkspaceSwitcher — lists the caller's ACTIVE workspaces and switches the
 * server-side active workspace. Selecting a workspace POSTs to
 * /api/v1/me/workspaces/switch (which validates the membership server-side)
 * then does a full page reload — the Pinia stores are tenant-scoped, so a hard
 * reload is the clean way to re-bootstrap everything under the new tenant.
 */
export default {
  name: 'WorkspaceSwitcher',
  props: {
    /** [{ tenant_id, name, role, is_home }] — ACTIVE memberships, home first. */
    workspaces:     { type: Array,  default: () => [] },
    /** The currently-active tenant id. */
    activeTenantId: { type: Number, default: 0 },
    /** Switch endpoint — overridable for non-standard mounts. */
    switchEndpoint: { type: String, default: '/api/v1/me/workspaces/switch' },
  },

  data() {
    return { open: false, anchor: null, switching: false };
  },

  computed: {
    activeName() {
      const active = this.workspaces.find((w) => w.tenant_id === this.activeTenantId);
      return active?.name || this.workspaces[0]?.name || 'Workspace';
    },
    panelStyle() {
      const a = this.anchor;
      if (!a) return {};
      return {
        position: 'fixed',
        top: `${Math.min(window.innerHeight - 8, a.bottom + 6)}px`,
        left: `${Math.max(8, a.left)}px`,
        minWidth: `${Math.max(200, a.width)}px`,
      };
    },
  },

  methods: {
    toggle() {
      if (this.open) { this.close(); return; }
      const el = this.$refs.triggerEl;
      if (el?.getBoundingClientRect) {
        const r = el.getBoundingClientRect();
        this.anchor = { bottom: r.bottom, left: r.left, width: r.width };
      }
      this.open = true;
    },
    close() { this.open = false; this.anchor = null; },

    async select(w) {
      if (this.switching || w.tenant_id === this.activeTenantId) { this.close(); return; }
      this.switching = true;
      try {
        await useApi().post(this.switchEndpoint, { tenant_id: w.tenant_id });
        // Tenant-scoped stores — hard reload to re-bootstrap under the new tenant.
        window.location.reload();
      } catch (_) {
        this.switching = false;
        this.close();
      }
    },
  },
};
</script>

<style scoped lang="scss">
.ws-switcher {
  width: 100%;

  &__trigger {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    width: 100%;
    padding: 0.25rem 0.4rem;
    border: 1px solid var(--theme-rail-hairline);
    border-radius: var(--theme-radius-sm, 5px);
    background: var(--theme-rail-hover-bg, transparent);
    color: var(--theme-rail-text-subtle);
    font-size: 0.68rem;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 100ms, color 100ms, border-color 100ms;

    &:hover {
      background: var(--theme-rail-active-bg);
      color: var(--theme-rail-text-strong);
      border-color: var(--theme-rail-zone-border);
    }
  }

  &__icon { opacity: 0.75; font-size: 0.66rem; flex-shrink: 0; }

  &__label {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: left;
  }

  &__chevron {
    flex-shrink: 0;
    font-size: 0.6rem;
    opacity: 0.6;
    transition: transform 150ms;
  }

  &.is-open &__chevron { transform: rotate(180deg); }
}
</style>

<style lang="scss">
/* Panel is teleported to <body>, so these are unscoped + namespaced. */
.ws-switcher__backdrop { position: fixed; inset: 0; z-index: 2040; }

.ws-switcher__panel {
  background: var(--theme-bg-card, #fff);
  border-radius: var(--theme-radius-lg, 8px);
  box-shadow: var(--theme-shadow-panel, 0 8px 28px rgba(0, 0, 0, 0.2));
  border: 1px solid var(--theme-border, #E8E3DC);
  padding: 0.3rem 0;
  z-index: 2050;
  max-height: 60vh;
  overflow-y: auto;
}

.ws-switcher__panel-head {
  padding: 0.35rem 0.85rem 0.3rem;
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--theme-text-muted, #8a8a8a);
}

.ws-switcher__item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.5rem 0.85rem;
  border: 0;
  background: none;
  text-align: left;
  font-family: inherit;
  font-size: 0.82rem;
  color: var(--theme-text-primary, #2A2B2E);
  cursor: pointer;

  &:hover { background: var(--theme-accent-light, #F5F1E9); }
  &:disabled { opacity: 0.6; cursor: default; }
  &.is-active { font-weight: 600; }
}

.ws-switcher__item-name { flex: 1 1 auto; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.ws-switcher__badge {
  flex-shrink: 0;
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: var(--theme-accent-primary, #8E7456);
  background: var(--theme-accent-light, #F5F1E9);
  padding: 0.05rem 0.35rem;
  border-radius: 3px;
}

.ws-switcher__check { flex-shrink: 0; color: var(--theme-accent-primary, #8E7456); font-size: 0.72rem; }
</style>
